import React, { useState, useEffect, useCallback } from 'react';
import emailApi from '../services/emailApi';
import { useToast } from '../hooks/useToast';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

interface Group {
  id: number;
  name: string;
  color: string;
  member_count: number;
}

const PRESET_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const ContactGroupsView: React.FC = () => {
  const { toast } = useToast();
  const { confirmState, confirm, cancelConfirm } = useConfirm();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
  const [groupEmails, setGroupEmails] = useState<string[]>([]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await emailApi.getGroups();
      setGroups(data as unknown as Group[]);
    } catch (err) {
      console.error('Error loading groups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.warning('Atenție', 'Numele grupului este obligatoriu');
      return;
    }
    try {
      await emailApi.createGroup(newName.trim(), newColor);
      toast.success('Succes', `Grupul "${newName}" a fost creat`);
      setNewName('');
      setShowForm(false);
      loadGroups();
    } catch {
      toast.error('Eroare', 'Nu am putut crea grupul');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const ok = await confirm({
      title: 'Confirmare ștergere',
      message: `Sigur vrei să ștergi grupul "${name}"?`,
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!ok) return;
    try {
      await emailApi.deleteGroup(id);
      toast.success('Succes', 'Grup șters');
      loadGroups();
    } catch {
      toast.error('Eroare', 'Nu am putut șterge grupul');
    }
  };

  const handleViewMembers = async (groupId: number) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
      return;
    }
    try {
      const emails = await emailApi.getGroupEmails(groupId);
      setGroupEmails(emails);
      setExpandedGroup(groupId);
    } catch {
      toast.error('Eroare', 'Nu am putut încărca membrii grupului');
    }
  };

  const handleCopyEmails = async (groupId: number) => {
    try {
      const emails = await emailApi.getGroupEmails(groupId);
      if (emails.length === 0) {
        toast.warning('Atenție', 'Grupul nu are membri');
        return;
      }
      await navigator.clipboard.writeText(emails.join(', '));
      toast.success('Copiat', `${emails.length} adrese copiate în clipboard`);
    } catch {
      toast.error('Eroare', 'Nu am putut copia adresele');
    }
  };

  const btnStyle = (bg: string, color: string = '#fff'): React.CSSProperties => ({
    padding: '6px 14px',
    backgroundColor: bg,
    color,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: 'var(--input-bg, #0f172a)',
    border: '1px solid var(--border-color, #334155)',
    borderRadius: '6px',
    color: 'var(--text-primary, #e2e8f0)',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: 'var(--text-primary, #e2e8f0)' }}>👥 Grupuri de Contacte</h2>
        <button style={btnStyle('#3b82f6')} onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Anulează' : '➕ Grup Nou'}
        </button>
      </div>

      {showForm && (
        <div style={{
          backgroundColor: 'var(--card-bg, #1e293b)',
          border: '1px solid var(--border-color, #334155)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary, #e2e8f0)' }}>➕ Grup Nou</h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Nume grup</label>
              <input style={inputStyle} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ex: Clienți VIP" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Culoare</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewColor(c)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: c,
                      border: newColor === c ? '3px solid #fff' : '2px solid transparent',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  />
                ))}
              </div>
            </div>
            <button style={btnStyle('#3b82f6')} onClick={handleCreate}>Creează</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary, #94a3b8)' }}>Se încarcă...</div>
      ) : groups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary, #94a3b8)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <p>Nu ai grupuri încă. Creează unul!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {groups.map(g => (
            <div key={g.id} style={{
              backgroundColor: 'var(--card-bg, #1e293b)',
              border: '1px solid var(--border-color, #334155)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: g.color || '#3b82f6'
                  }} />
                  <span style={{
                    padding: '4px 12px',
                    backgroundColor: (g.color || '#3b82f6') + '22',
                    color: g.color || '#3b82f6',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    {g.name}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>
                    {g.member_count || 0} membri
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={btnStyle('#3b82f6')} onClick={() => handleViewMembers(g.id)}>
                    {expandedGroup === g.id ? '🔼' : '👁️'} Membri
                  </button>
                  <button style={btnStyle('#10b981')} onClick={() => handleCopyEmails(g.id)}>
                    📋 Copiază
                  </button>
                  <button style={btnStyle('#ef4444')} onClick={() => handleDelete(g.id, g.name)}>
                    🗑️
                  </button>
                </div>
              </div>
              {expandedGroup === g.id && (
                <div style={{
                  borderTop: '1px solid var(--border-color, #334155)',
                  padding: '12px 20px',
                  backgroundColor: 'var(--input-bg, #0f172a)'
                }}>
                  {groupEmails.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '13px', margin: 0 }}>Niciun membru în acest grup</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {groupEmails.map((em, i) => (
                        <span key={i} style={{
                          padding: '4px 10px',
                          backgroundColor: 'var(--card-bg, #1e293b)',
                          border: '1px solid var(--border-color, #334155)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: 'var(--text-primary, #e2e8f0)'
                        }}>
                          {em}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

export default ContactGroupsView;
